/**
 * RWBR — BRING YOUR OWN MODEL. D-EZ-48, the panel.
 *
 * Howard, 18 Aug 2026: *"we need to add the same Login option where a user OTHER
 * than me puts their own api model in and key and the ai uses their api key for
 * the commentary and such."*
 *
 * ===========================================================================
 * WHAT THIS SCREEN OWES A PLAYER, IN ORDER
 * ===========================================================================
 *
 * A form that asks a stranger for an API key has three obligations, and most of
 * them get one:
 *
 *  1. SAY WHAT HAPPENS WITHOUT ONE — first, and without shame. The game is
 *     complete with no key: the booth speaks its written lines and the paper is
 *     written by the game. Leading with "unlock AI features" would be a lie by
 *     framing.
 *  2. SAY WHERE THE KEY GOES. It is kept in this browser, it travels to this
 *     app's server on each call, and it is spent there and dropped. Written in
 *     the panel, in those words, not in a tooltip.
 *  3. SAY WHAT IT WILL COST. A number, not a shrug. The pricing is quoted for
 *     the model this game is tuned for and marked as of the date it was checked,
 *     because a price in an interface with no date on it is a promise that ages
 *     into a lie.
 *
 * ⚠ THE KEY IS NEVER RENDERED. Once it is locked in, the panel shows
 * `maskKey()` and there is no way to reveal it — a key you can read off the
 * screen is a key somebody behind you can read off the screen, and the only
 * thing a player ever needs to do with a stored key is replace it.
 */

import { useState } from 'react';
import { DEFAULT_MODEL, maskKey, SUGGESTED_MODELS, useAiSetup } from '../lib/aiKey.ts';

export interface AiKeyPanelProps {
  /**
   * True when the signed-in account is the owner's — the only account the
   * site's own key is spent for (D-EZ-100: "any user brings their own. Mine is
   * for me."). Everyone else sees "written by the game" until they bring a key.
   */
  serverHasKey?: boolean;
  /** Shown as the current state when known. */
  onChanged?: () => void;
  /**
   * D-EZ-103 — open with the explanation and the form showing. The strip's
   * Commentary button opens this panel on its own, so pressing it should land
   * on the answer, not on a second button.
   */
  startOpen?: boolean;
}

export default function AiKeyPanel({ serverHasKey = false, onChanged, startOpen = false }: AiKeyPanelProps) {
  const { setup, save, forget } = useAiSetup();
  const [open, setOpen] = useState(startOpen);
  const [key, setKey] = useState('');
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const chosen = SUGGESTED_MODELS.find((m) => m.id === model);

  const lockIn = () => {
    const result = save(key, model, remember);
    if (!result.ok) {
      setError(result.error ?? 'that key was not accepted');
      return;
    }
    setError(null);
    setKey('');
    setOpen(false);
    onChanged?.();
  };

  const drop = () => {
    forget();
    setKey('');
    setError(null);
    onChanged?.();
  };

  return (
    <section className="aikey" aria-label="Bring your own model">
      <header className="aikey__head">
        <div>
          <h3>The commentary and the newspaper</h3>
          <p className="aikey__state">
            {setup ? (
              <>
                <b className="aikey__on">ON YOUR KEY</b> {setup.model} · {maskKey(setup.key)} ·{' '}
                {setup.remembered ? 'remembered on this browser' : 'this tab only'}
              </>
            ) : serverHasKey ? (
              <>
                <b className="aikey__server">ON THE SITE KEY</b> You are signed in as the site&rsquo;s owner,
                so the booth and the paper use the owner&rsquo;s own key. Nobody else&rsquo;s game spends it.
              </>
            ) : (
              <>
                <b className="aikey__off">WRITTEN BY THE GAME</b> No AI model is called. The booth speaks
                its written lines and the paper is written by the game — everything works.
              </>
            )}
          </p>
        </div>
        <button type="button" className="btn btn--quiet btn--tiny" onClick={() => setOpen((v) => !v)}>
          {open ? 'Close' : setup ? 'Change' : 'Use my own model'}
        </button>
      </header>

      {open && (
        <div className="aikey__body">
          {/*
            OBLIGATION ONE — what happens without a key, said first and said
            without shame. A player who reads this and closes the panel has made
            an informed choice, not settled for less.
          */}
          <p className="aikey__note">
            <b>You do not need a key.</b> Without one the three-man booth speaks its written lines, The
            Stock Car Dispatch is written by the game itself, and drivers get the built-in names —
            nothing is locked. To hear the booth&rsquo;s AI voice, bring your own OpenAI key: it pays for
            your requests only. The site owner&rsquo;s key is the owner&rsquo;s, and is never spent on
            anyone else&rsquo;s game.
          </p>

          <label className="aikey__field">
            <span>Model</span>
            <input
              list="rwbr-models"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder={DEFAULT_MODEL}
              spellCheck={false}
              autoComplete="off"
            />
            <datalist id="rwbr-models">
              {SUGGESTED_MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </datalist>
          </label>
          {chosen && <p className="aikey__hint">{chosen.note}</p>}
          {!chosen && model.trim().length > 0 && (
            <p className="aikey__hint">
              Not one of the two suggested — that is fine, any model id your account can reach will
              be sent as typed.
            </p>
          )}

          <label className="aikey__field">
            <span>OpenAI API key</span>
            <input
              type="password"
              value={key}
              onChange={(e) => {
                setKey(e.target.value);
                setError(null);
              }}
              placeholder="sk-…"
              spellCheck={false}
              autoComplete="off"
              // A key in a password manager's autofill history is a key in a
              // place its owner did not choose to put it.
              data-1p-ignore
            />
          </label>

          <label className="aikey__check">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            <span>
              Remember it on this browser. Untick on a shared machine and it is forgotten when the tab
              closes.
            </span>
          </label>

          {error && (
            <p className="err" role="alert">
              {error}
            </p>
          )}

          <div className="aikey__buttons">
            <button type="button" className="btn" onClick={lockIn} disabled={key.trim().length === 0}>
              Lock it in
            </button>
            {setup && (
              <button type="button" className="btn btn--quiet" onClick={drop}>
                Forget my key
              </button>
            )}
          </div>

          {/*
            OBLIGATIONS TWO AND THREE — where it goes and what it costs, in the
            panel rather than in a tooltip, and with a date on the price.
          */}
          <div className="aikey__small">
            <p>
              <b>Where it goes.</b> Into this browser only — <em>this tab</em>, or this browser if you
              ticked remember. It is never saved into a league, never written to the database and never
              put in a link. On each call it travels to this site’s own server, which spends it on that
              one request and keeps nothing. It is never shown on screen again after you lock it in.
            </p>
            <p>
              <b>What it costs.</b> GPT-6 Luna was $0.10 per million input tokens and $0.50 per
              million output as of 22 Sep 2026; GPT-6 Sol was $2 and $10, roughly twenty times as
              much. A race weekend of commentary is a few thousand tokens and a newspaper article a
              few thousand more, so a full 36-race season on Luna lands in the region of a few cents
              to a few tens of cents. Check current pricing at OpenAI before you rely on that.
            </p>
            <p>
              <b>Be sensible with it.</b> Use a key made for this, put a spend limit on it in your
              OpenAI account, and revoke it there if you ever want it gone — nothing here breaks when
              you do. The game simply goes back to writing its own.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
